import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import Step1Username from './Step1Username';
import Step2PersonalInfo from './Step2PersonalInfo';
import Step3CarDetails from './Step3CarDetails';
import Step4FavoriteLocation from './Step4FavoriteLocation';
import Spinner from '../common/Spinner';
import { logProfileError, logProfileAction, logProfilePerformance } from '../../utils/errorLogger';
import './ProfileComplete.css';

function ProfileCompleteContainer() {
  const { t } = useTranslation('common');
  const { refreshProfile } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [userData, setUserData] = useState(null);
  const [step1Data, setStep1Data] = useState(null); // Store step 1 data for country
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startTime] = useState(Date.now()); // Track total completion time
  const navigate = useNavigate();

  // Container ref for mouse tracking and orbs
  const containerRef = useRef(null);
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get authenticated session
        const { data: sessionData, error: authError } = await supabase.auth.getSession();
        
        if (authError) {
          throw authError;
        }

        if (!sessionData.session) {
          navigate('/login', { replace: true });
          return;
        }

        const user = sessionData.session.user;
        console.log('ProfileComplete: User authenticated:', user.id);

        // For new users, check if they have a profile first before fetching
        // This prevents unnecessary 406 errors
        let profile = null;
        try {
          console.log('Checking if profile exists for user:', user.id);
          const { data: profileData, error: profileError, count } = await supabase
            .from('profiles')
            .select('id, completion_step, username, language, city, avatar_url, first_name, last_name, year_of_birth, gender', { count: 'exact' })
            .eq('id', user.id);

          if (profileError) {
            console.error('Profile fetch error:', profileError);
            // Continue with null profile - this might be a new user
            profile = null;
          } else if (profileData && profileData.length > 0) {
            profile = profileData[0];
            console.log('Profile found:', profile);
          } else {
            console.log('No profile found - this is normal for new users');
            profile = null;
          }
        } catch (error) {
          console.log('Profile check failed - assuming new user:', error.message);
          profile = null;
        }

        // Combine user and profile data
        setUserData({ ...user, profile: profile || {} });

        // Check completion status and redirect accordingly
        if (profile?.completion_step !== null && profile?.completion_step !== undefined && profile.completion_step >= 3) {
          console.log('✅ Profile complete (step', profile.completion_step, '), redirecting to home');
          navigate('/home', { replace: true });
          return;
        }

        // Set current step based on profile (default to 1 for new users)
        // Handle completion_step = 0 by starting at step 1, but preserve the database value
        let startStep;
        if (profile?.completion_step === 0) {
          startStep = 1; // UI starts at step 1, but profile completion_step remains 0
        } else {
          startStep = profile?.completion_step ?? 1;
        }
        setCurrentStep(startStep);
        
        console.log('🚀 Profile setup starting at step:', startStep);
        console.log('📊 Profile state:', {
          hasProfile: !!profile,
          completionStep: profile?.completion_step,
          hasUsername: !!profile?.username,
          hasLocation: !!profile?.city
        });

      } catch (error) {
        console.error('Error:', error.message);
        logProfileError(error, 'container', { action: 'session_and_profile_fetch' });
        setError(error.message || t('profile_setup.error_generic'));
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate, t]);

  // Go back to the previous step (in-session; the DB completion_step is unchanged).
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleStepComplete = async (stepData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Store step 1 data for use in step 2
      if (currentStep === 1) {
        setStep1Data(stepData);
        console.log('💾 Step 1 data stored:', stepData);
      }
      
      if (!userData?.id) {
        throw new Error(t('profile_setup.error_no_user'));
      }

      const nextStep = currentStep + 1;
      
      // Check if profile exists, create or update accordingly
      if (!userData.profile || Object.keys(userData.profile).length === 0) {
        // Create new profile for first-time users
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userData.id,
            email: userData.email,
            ...stepData,
            completion_step: nextStep,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          throw insertError;
        }
        
        console.log('Profile created successfully for step:', currentStep);
      } else {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            ...stepData,
            completion_step: nextStep,
            updated_at: new Date().toISOString()
          })
          .eq('id', userData.id);

        if (updateError) {
          throw updateError;
        }
        
        console.log('Profile updated successfully for step:', currentStep);
      }

      // Update local user data
      setUserData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...stepData,
          completion_step: nextStep
        }
      }));

      if (nextStep <= 3) {
        setCurrentStep(nextStep);
        logProfileAction('step_advanced', `step${currentStep}`, { fromStep: currentStep, toStep: nextStep });
      } else {
        // Required steps are done — show the optional favorite-location step
        // (the re-engagement hook) before finishing.
        const totalTime = Date.now() - startTime;
        logProfilePerformance('total_setup_time', totalTime, 'complete');
        logProfileAction('profile_setup_completed', 'complete', { totalTime, stepsCompleted: 3 });
        setCurrentStep(4);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      logProfileError(error, `step${currentStep}`, {
        action: 'profile_update',
        step: currentStep,
        dataKeys: Object.keys(stepData)
      });
      setError(error.message || t('profile_setup.error_update'));
      
      // If it's a constraint violation (duplicate username), provide specific feedback
      if (error.message && error.message.includes('duplicate key value violates unique constraint')) {
        if (error.message.includes('username')) {
          setError(t('errors.username_taken'));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userData?.id) {
        throw new Error(t('profile_setup.error_no_user'));
      }

      const nextStep = currentStep + 1;

      // Check if profile exists, create or update accordingly
      if (!userData.profile || Object.keys(userData.profile).length === 0) {
        // Create minimal profile for first-time users
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userData.id,
            email: userData.email,
            completion_step: nextStep,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          throw insertError;
        }
        
        console.log('Minimal profile created when skipping step:', currentStep);
      } else {
        // Update only the completion_step
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            completion_step: nextStep,
            updated_at: new Date().toISOString()
          })
          .eq('id', userData.id);

        if (updateError) {
          throw updateError;
        }
        
        console.log('Profile step skipped:', currentStep);
      }

      // Update local user data
      setUserData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          completion_step: nextStep
        }
      }));

      if (nextStep <= 3) {
        setCurrentStep(nextStep);
      } else {
        // Passengers / package users also reach the optional location step.
        setCurrentStep(4);
      }
    } catch (error) {
      console.error('Error in handleSkip:', error);
      setError(error.message || t('profile_setup.error_skip'));
    } finally {
      setLoading(false);
    }
  };

  // Finish onboarding (called from the optional favorite-location step): refresh
  // the profile in AuthContext so country filtering works, then go to Home.
  const handleFinishOnboarding = async () => {
    try {
      await refreshProfile();
      await new Promise((resolve) => setTimeout(resolve, 300));
    } catch (err) {
      console.warn('Could not refresh AuthContext after onboarding:', err);
    }
    navigate('/home', { replace: true });
  };

  // Handle error retry
  const handleRetry = () => {
    setError(null);
    if (userData) {
      // If we have user data, just retry current step
      setLoading(false);
    } else {
      // Otherwise reload the page to restart the flow
      window.location.reload();
    }
  };

  // If loading, show spinner
  if (loading) {
    return (
      <div className="pcf-loading">
        <Spinner size="large" customText={t('profile_setup.loading')} />
      </div>
    );
  }

  // If error, show error message with retry button
  if (error) {
    return (
      <div className="pcf-error">
        <h2>{t('profile_setup.error_title')}</h2>
        <p>{error}</p>
        <button 
          className="pcf-retry-button" 
          onClick={handleRetry}
        >
          {t('buttons.retry')}
        </button>
      </div>
    );
  }

  // Main render - Enhanced with login page styling
  return (
    <div ref={containerRef} className="enhanced-profile-complete-page">

      {/* Profile Complete Logo */}
      <div className="profile-complete-logo-container">
        <h1 className="profile-complete-logo">
          <span className="profile-logo-text" data-text="Complete Profile">{t('profile_setup.complete_profile')}</span>
          <div className="profile-logo-glow" />
        </h1>
      </div>

      {/* Profile form container with orbs */}
      <div className="profile-form-container">
          {currentStep > 1 && (
            <button
              type="button"
              className="pcf-back-button"
              onClick={handleBack}
              aria-label={t('buttons.back')}
            >
              <FaArrowLeft /> {t('buttons.back')}
            </button>
          )}
          {currentStep <= 3 && (
            <div className="pcf-step-indicator">
              <div className="pcf-step-progress">
                <div className={`pcf-step-bullet ${currentStep >= 1 ? 'pcf-active' : ''}`}>1</div>
                <div className={`pcf-step-line ${currentStep >= 2 ? 'pcf-active' : ''}`}></div>
                <div className={`pcf-step-bullet ${currentStep >= 2 ? 'pcf-active' : ''}`}>2</div>
                <div className={`pcf-step-line ${currentStep >= 3 ? 'pcf-active' : ''}`}></div>
                <div className={`pcf-step-bullet ${currentStep >= 3 ? 'pcf-active' : ''}`}>3</div>
              </div>
              <p className="pcf-step-description">
                {currentStep === 1 && t('profile_setup.step1_description')}
                {currentStep === 2 && t('profile_setup.step2_description')}
                {currentStep === 3 && t('profile_setup.step3_description')}
              </p>
            </div>
          )}

          {userData ? (
            <>
              {currentStep === 1 && (
                <Step1Username
                  userData={userData}
                  onComplete={handleStepComplete}
                />
              )}

              {currentStep === 2 && (
                <Step2PersonalInfo
                  userData={{ ...userData, step1Data }} // Pass step1Data
                  onComplete={handleStepComplete}
                />
              )}

              {currentStep === 3 && (
                <Step3CarDetails
                  userData={userData}
                  onComplete={handleStepComplete}
                  onSkip={handleSkip}
                />
              )}

              {currentStep === 4 && (
                <Step4FavoriteLocation
                  userData={userData}
                  onComplete={handleFinishOnboarding}
                />
              )}
            </>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>{t('app.loading_profile')}</p>
              <Spinner size="medium" />
            </div>
          )}
      </div>
    </div>
  );
}

export default ProfileCompleteContainer;